import React from 'react';
import isEqual from 'lodash.isequal';
import { SortOptions } from 'models/interfaces/PageCache';
import { TableHeading, TableRow } from 'models/interfaces/Table';
import { IProductFulfillmentAudit, CONSTANTS } from '@tpco/tpco-saturn-types';
import { p3PoitnsAlgorithm } from '@tpco/tpco-saturn-logic';
import { printDate } from 'util/dateUtils';
import { Link } from 'react-router-dom';
import { OkStatus, FailedStatus } from '../Icons/StatusIcons';
import { DeleteIcon } from '../Icons/EditIcons';

const makeProductAduitHeadings = (sortOptions: SortOptions) => {
  const {
    advertiser_name,
    tgrams_id,
    campaign_name,
    owner,
    status,
    quantity,
    allocatedPoints,
    valid,
    opportunity_id,
    item_id
  } = sortOptions;
  const productFulfillmentHeading: TableHeading[] = [
    {
      key: 'tgrams_id',
      value: 'Company ID',
      style: 'align-right',
      sort: true,
      direction: tgrams_id ? tgrams_id : 'DESC'
    },

    {
      key: 'advertiser_name',
      value: 'Company Name',
      sort: true,
      direction: advertiser_name ? advertiser_name : 'ASC'
    },
    {
      key: 'campaign_name',
      value: 'Campaign',
      sort: true,
      direction: campaign_name ? campaign_name : 'DESC'
    },
    {
      key: 'owner',
      value: 'Owner',
      sort: true,
      direction: owner ? owner : 'DESC'
    },
    {
      key: 'status',
      value: 'Status',
      sort: true,
      direction: status ? status : 'DESC'
    },
    {
      key: 'start_date',
      value: 'Start Date',
      style: 'align-right'
    },
    {
      key: 'end_date',
      value: 'End Date',
      style: 'align-right'
    },
    {
      key: 'created_date',
      value: 'Created Date',
      style: 'align-right'
    },
    {
      key: 'product',
      value: 'Product'
    },
    {
      key: 'opportunity_id',
      value: 'Opportunity ID',
      sort: true,
      direction: opportunity_id ? opportunity_id : 'DESC'
    },
    {
      key: 'item_id',
      value: 'Item ID',
      sort: true,
      direction: item_id ? item_id : 'DESC'
    },
    {
      key: 'quantity',
      value: 'Quantity',
      style: 'align-right',
      sort: true,
      direction: quantity ? quantity : 'DESC'
    },
    {
      key: 'allocatedPoints',
      value: 'Allocated Points',
      style: 'align-right',
      sort: true,
      direction: allocatedPoints ? allocatedPoints : 'DESC'
    },
    {
      key: 'valid',
      value: 'Valid',
      sort: true,
      style: 'align-center',
      direction: valid ? valid : 'DESC'
    }
  ];
  return productFulfillmentHeading;
};
const sortFulfillmentAudit = (
  fulfillments: IProductFulfillmentAudit[],
  sortKey: string,
  sortDirection: string
) => {
  const sorted = fulfillments.sort((c: IProductFulfillmentAudit, d: IProductFulfillmentAudit) => {
    let a, b;
    let sortString = true;
    switch (sortKey) {
      case 'advertiser_name':
        a = c.account_name;
        b = d.account_name;
        break;
      case 'tgrams_id':
        a = c.tgrams_id;
        b = d.tgrams_id;
        sortString = false;
        break;
      case 'campaign_name':
        a = c.campaign_name;
        b = d.campaign_name;
        break;
      case 'owner':
        a = c.owner;
        b = d.owner;
        break;
      case 'opportunity_id':
        a = c.opportunity_id;
        b = d.opportunity_id;
        break;
      case 'item_id':
        a = c.item_id;
        b = d.item_id;
        break;
      case 'status':
        a = CONSTANTS.CAMPAIGN_STATUS_MAP[c.status_id];
        b = CONSTANTS.CAMPAIGN_STATUS_MAP[d.status_id];
        break;
      case 'quantity':
        a = c.quantity;
        b = d.quantity;
        sortString = false;
        break;
      case 'allocatedPoints':
        a = 0;
        b = 0;
        sortString = false;
        if (c.rank_points != null && c.coverage_areas != null) {
          a = p3PoitnsAlgorithm(c.rank_points, c.coverage_areas);
        }
        if (d.rank_points != null && d.coverage_areas != null) {
          b = p3PoitnsAlgorithm(d.rank_points, d.coverage_areas);
        }

        break;
      case 'valid':
        a = 'invalid';
        b = 'invalid';
        if (c.rank_points != null && c.coverage_areas != null) {
          a = isEqual(p3PoitnsAlgorithm(c.rank_points, c.coverage_areas), c.quantity)
            ? 'valid'
            : 'invalid';
        }
        if (d.rank_points != null && d.coverage_areas != null) {
          b = isEqual(p3PoitnsAlgorithm(d.rank_points, d.coverage_areas), d.quantity)
            ? 'valid'
            : 'invalid';
        }

        break;
      default:
        a = c.account_name;
        b = d.account_name;
    }
    if (sortDirection === 'ASC') {
      if (sortString) {
        if (a < b) {
          return -1;
        }
        if (a > b) {
          return 1;
        }
        return 0;
      }
      return a - b;
    } else {
      if (sortString) {
        if (b < a) {
          return -1;
        }
        if (b > a) {
          return 1;
        }
        return 0;
      }
      return b - a;
    }
  });
  return sorted;
};

const buildRenderDataFromAuditProps = (fulfills: any[]) => {
  return fulfills.reduce<[Map<string, any>, TableRow[]]>(
    ([map, rows], x) => {
      // shallow copy thomas Advertiser to save from disappearing due to no-cache
      const copy: IProductFulfillmentAudit = { ...x };
      // add copy to map
      map.set(copy.tgrams_id, copy);
      // create template tr and add to array
      const tr = createFulfillmentRow(copy);
      rows.push(tr);

      return [map, rows];
    },
    [new Map<string, IProductFulfillmentAudit>(), new Array<TableRow>()]
  );
};

const buildRenderDataFromAuditExportProps = (fulfills: any[]) => {
  return fulfills.reduce<[Map<string, any>, TableRow[]]>(
    ([map, rows], x) => {
      // shallow copy thomas Advertiser to save from disappearing due to no-cache
      const copy: IProductFulfillmentAudit = { ...x };
      // add copy to map
      map.set(copy.tgrams_id, copy);
      // create template tr and add to array
      const tr = createFulfillmentCSVRow(copy);
      rows.push(tr);

      return [map, rows];
    },
    [new Map<string, IProductFulfillmentAudit>(), new Array<TableRow>()]
  );
};

const createFulfillmentRow = (productFulfillment: IProductFulfillmentAudit) => {
  const fulfillmentRow: TableRow = { ...productFulfillment };
  fulfillmentRow.campaign_name = (
    <Link
      to={`/advertisers/${productFulfillment.tgrams_id}/campaigns/${
        productFulfillment.campaign_id
      }/edit`}
    >
      {productFulfillment.campaign_name}
    </Link>
  );
  fulfillmentRow.advertiser_name = (
    <Link to={`/advertisers/${productFulfillment.tgrams_id}`}>
      {productFulfillment.account_name}
    </Link>
  );
  fulfillmentRow.start_date = productFulfillment.start_date
    ? printDate(productFulfillment.start_date)
    : '';
  fulfillmentRow.created_date = productFulfillment.created_date
    ? printDate(productFulfillment.created_date)
    : '';
  fulfillmentRow.end_date = productFulfillment.end_date
    ? printDate(productFulfillment.end_date)
    : '';

  fulfillmentRow.status = CONSTANTS.CAMPAIGN_STATUS_MAP[productFulfillment.status_id];

  fulfillmentRow.product = productFulfillment.product_sku;
  fulfillmentRow.allocatedPoints = 0;
  if (productFulfillment.rank_points != null && productFulfillment.coverage_areas != null) {
    fulfillmentRow.allocatedPoints = p3PoitnsAlgorithm(
      productFulfillment.rank_points,
      productFulfillment.coverage_areas
    );
  }
  const validRegionPoints = productFulfillment.valid_region_points === 1;

  // console.log('valid regional points', {
  //   name: productFulfillment.account_name,
  //   valid_region: productFulfillment.valid_region_points,
  //   valid_output: validRegionPoints
  // });
  const valid =
    isEqual(fulfillmentRow.allocatedPoints, fulfillmentRow.quantity) && validRegionPoints;
  const validIcon = valid ? <OkStatus /> : <FailedStatus />;
  const validClass = valid ? 'child-campaign__status--valid' : 'child-campaign__status--invalid';
  fulfillmentRow.valid = <div className={validClass}>{validIcon}</div>;

  return fulfillmentRow;
};
const createFulfillmentCSVRow = (productFulfillment: IProductFulfillmentAudit) => {
  const fulfillmentRow: TableRow = {};
  fulfillmentRow.company_name = productFulfillment.account_name;
  fulfillmentRow.campaign_name = productFulfillment.campaign_name;
  fulfillmentRow.owner = productFulfillment.owner;
  fulfillmentRow.start_date = productFulfillment.start_date
    ? printDate(productFulfillment.start_date)
    : '';
  fulfillmentRow.created_date = productFulfillment.created_date
    ? printDate(productFulfillment.created_date)
    : '';
  fulfillmentRow.end_date = productFulfillment.end_date
    ? printDate(productFulfillment.end_date)
    : '';

  fulfillmentRow.status = CONSTANTS.CAMPAIGN_STATUS_MAP[productFulfillment.status_id];

  fulfillmentRow.product = productFulfillment.product_sku;
  fulfillmentRow.opportunity_id = productFulfillment.opportunity_id;
  fulfillmentRow.tgrams_id = productFulfillment.tgrams_id;
  fulfillmentRow.item_id = productFulfillment.item_id;
  fulfillmentRow.quantity = productFulfillment.quantity;
  fulfillmentRow.allocatedPoints = 0;
  const validRegionPoints = productFulfillment.valid_region_points === 1;

  if (productFulfillment.rank_points != null && productFulfillment.coverage_areas != null) {
    fulfillmentRow.allocatedPoints = p3PoitnsAlgorithm(
      productFulfillment.rank_points,
      productFulfillment.coverage_areas
    );
  }
  fulfillmentRow.valid =
    isEqual(fulfillmentRow.allocatedPoints, fulfillmentRow.quantity) && validRegionPoints
      ? 'valid'
      : 'invalid';
  return fulfillmentRow;
};
export {
  makeProductAduitHeadings,
  buildRenderDataFromAuditProps,
  buildRenderDataFromAuditExportProps,
  sortFulfillmentAudit
};
